import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { useAuth } from "../authentication/AuthContext";
import {
  Layout,
  UtensilsCrossed,
  Grid,
  Users,
  Package,
  ChefHat,
  History,
  UserCheck,
  TrendingDown,
  Zap,
  Tv,
  Settings,
  CreditCard,
  Shield,
  BarChart3,
  Sliders,
  Tag,
  BookOpen,
  Clock,
  FileSpreadsheet,
  Megaphone,
  Recycle,
  MessageSquareHeart,
  GitBranch,
} from "lucide-react";
import HappyHourSettings from "../HappyHourSettings";
import GstReportModal from "../GstReportModal";
import MarketingPanel from "../MarketingPanel";
import WastePanel from "../WastePanel";
import FeedbackPanel from "../FeedbackPanel";
import BranchesPanel from "../BranchesPanel";

// ─── Card data ─────────────────────────────────────────────────────────────
// Cards with `to` navigate; cards with `modal` open an in-page panel.
const buildCards = (registrationType, role, isStaff) => {
  const cards = [
    {
      to: "/structure",
      icon: Layout,
      label: "Restaurant Structure",
      description: "Manage floors, tables & sections",
      color: "from-indigo-500 to-indigo-700",
      badge: null,
      roles: ["manager"],
    },
    {
      to: "/add-product",
      icon: UtensilsCrossed,
      label: "Products",
      description: "Add & manage menu items",
      color: "from-teal-500 to-teal-700",
      badge: null,
      roles: ["manager"],
    },
    {
      to: "/categories",
      icon: Grid,
      label: "Categories",
      description: "Organise product categories",
      color: "from-cyan-500 to-cyan-700",
      badge: null,
      roles: ["manager"],
    },
    {
      to: "/staff",
      icon: Users,
      label: "Staff Management",
      description: "Add, edit & manage staff roles",
      color: "from-violet-500 to-violet-700",
      badge: null,
      roles: ["manager"],
    },
    {
      to: "/inventory",
      icon: Package,
      label: "Inventory",
      description: "Track stock & raw materials",
      color: "from-amber-500 to-amber-700",
      badge: null,
      roles: ["manager"],
    },
    {
      to: "/recipes",
      icon: ChefHat,
      label: "Recipes",
      description: "Link ingredients to menu items",
      color: "from-rose-500 to-rose-700",
      badge: null,
      roles: ["manager"],
    },
    {
      to: "/history",
      icon: History,
      label: "Order History",
      description: "View & export all past orders",
      color: "from-slate-500 to-slate-700",
      badge: null,
      roles: ["manager", "cashier"],
    },
    {
      to: "/customers",
      icon: UserCheck,
      label: "Customers",
      description: "CRM, loyalty & wallet info",
      color: "from-green-500 to-green-700",
      badge: null,
      roles: ["manager", "cashier"],
    },
    {
      to: "/expensises",
      icon: TrendingDown,
      label: "Expenses",
      description: "Track operational costs",
      color: "from-orange-500 to-orange-700",
      badge: null,
      roles: ["manager", "cashier"],
    },
    {
      to: "/ledger",
      icon: BookOpen,
      label: "Ledger Book",
      description: "Dues, payments & vendor credit",
      color: "from-indigo-500 to-indigo-700",
      badge: null,
      roles: ["manager", "cashier"],
    },
    ...(registrationType === "restaurant"
      ? [
          {
            to: "/process",
            icon: Zap,
            label: "Order Process",
            description: "Live kitchen order management",
            color: "from-red-500 to-red-700",
            badge: null,
            roles: ["manager", "cashier", "waiter", "chef"],
          },
          {
            to: "/kds",
            icon: Tv,
            label: "KDS Dashboard",
            description: "Kitchen display system",
            color: "from-orange-500 to-red-650",
            badge: null,
            roles: ["manager", "chef"],
          },
        ]
      : []),
    // ── Feature panels (open in place) ──────────────────────────────────
    {
      modal: "gst",
      icon: FileSpreadsheet,
      label: "GST & Tally",
      description: "Tax summary, CSV & Tally XML export",
      color: "from-emerald-500 to-emerald-700",
      badge: null,
      roles: ["manager"],
    },
    {
      modal: "marketing",
      icon: Megaphone,
      label: "Marketing",
      description: "WhatsApp / SMS campaigns & segments",
      color: "from-pink-500 to-pink-700",
      badge: null,
      roles: ["manager"],
    },
    {
      modal: "happyHours",
      icon: Clock,
      label: "Happy Hours",
      description: "Time-based automatic discounts",
      color: "from-amber-500 to-orange-600",
      badge: null,
      roles: ["manager"],
    },
    {
      modal: "waste",
      icon: Recycle,
      label: "Waste Log",
      description: "Spoilage tracking & cost impact",
      color: "from-red-500 to-rose-700",
      badge: null,
      roles: ["manager"],
    },
    {
      modal: "feedback",
      icon: MessageSquareHeart,
      label: "Customer Feedback",
      description: "Ratings dashboard & feedback QR",
      color: "from-yellow-500 to-amber-600",
      badge: null,
      roles: ["manager"],
    },
    ...(!isStaff
      ? [
          {
            modal: "branches",
            icon: GitBranch,
            label: "Branches",
            description: "Multi-outlet switch & comparison",
            color: "from-cyan-500 to-sky-700",
            badge: null,
            roles: [],
          },
        ]
      : []),
    // ────────────────────────────────────────────────────────────────────
    {
      to: "/profile",
      icon: Settings,
      label: "Profile & Settings",
      description: "Store info, GST, UPI & loyalty",
      color: "from-gray-500 to-gray-700",
      badge: null,
      roles: ["manager"],
    },
    {
      to: "/profile#security",
      icon: Shield,
      label: "Security & Devices",
      description: "Reset password & active login devices",
      color: "from-amber-500 to-amber-700",
      badge: null,
      roles: ["manager"],
    },
    {
      to: "/subscription-card",
      icon: CreditCard,
      label: "Subscription",
      description: "Manage your plan & billing",
      color: "from-purple-500 to-purple-700",
      badge: null,
      roles: ["manager"],
    },
    ...(Number(role) === 2
      ? [
          {
            to: "/superadmindashboard",
            icon: Shield,
            label: "Super Admin",
            description: "Platform-wide administration",
            color: "from-yellow-500 to-yellow-700",
            badge: "ADMIN",
            roles: [],
          },
        ]
      : []),
    {
      to: "/analytics",
      icon: BarChart3,
      label: "Analytics",
      description: "Revenue charts & insights",
      color: "from-blue-500 to-blue-700",
      badge: null,
      roles: ["manager"],
    },
    {
      to: "/offers-management",
      icon: Tag,
      label: "Offers",
      description: "Create and manage customer discounts",
      color: "from-pink-500 to-pink-700",
      badge: null,
      roles: ["manager"],
    },
  ];

  return cards;
};

// ─── AdminPanel ─────────────────────────────────────────────────────────────
const AdminPanel = () => {
  const [registrationType, setRegistrationType] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const { role, isStaff } = useAuth();

  useEffect(() => {
    AOS.init({ duration: 500, once: true });
    const type = localStorage.getItem("registrationType");
    setRegistrationType(type);
  }, []);

  const cards = buildCards(registrationType, role, isStaff);

  const cardInner = (card) => (
    <>
      {/* Badge */}
      {card.badge && (
        <span
          className={`absolute top-2 right-2 text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wider ${
            card.badge === "SOON"
              ? "bg-green-150 text-green-700"
              : "bg-yellow-400 text-yellow-900 animate-pulse"
          }`}
        >
          {card.badge}
        </span>
      )}

      {/* Lucide Icon in gradient circle */}
      <div
        className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${card.color} flex items-center justify-center text-white mb-3 shadow-md group-hover:scale-105 transition-transform duration-200`}
      >
        <card.icon className="w-5 h-5" />
      </div>

      <h3 className="text-xs font-black text-gray-800 text-center leading-tight">
        {card.label}
      </h3>
      <p className="text-[10px] text-gray-400 text-center mt-1 leading-snug hidden sm:block">
        {card.description}
      </p>
    </>
  );

  const cardClass =
    "relative group flex flex-col items-center justify-center bg-white rounded-2xl p-5 shadow-sm border border-gray-150 hover:border-gray-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 w-full";

  return (
    <div className="min-h-screen bg-slate-50 mt-14">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-5 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <div className="p-2.5 bg-slate-100 rounded-2xl text-slate-700">
            <Sliders className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-800">
              Admin Panel
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage every aspect of your restaurant from one place
            </p>
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {cards.map((card, idx) =>
            card.modal ? (
              <button
                key={"modal-" + card.modal}
                onClick={() => setActiveModal(card.modal)}
                data-aos="fade-up"
                data-aos-delay={idx * 30}
                className={cardClass}
              >
                {cardInner(card)}
              </button>
            ) : (
              <Link
                key={card.to + card.label}
                to={card.to || "#"}
                data-aos="fade-up"
                data-aos-delay={idx * 30}
                className={`${cardClass} ${!card.to ? "cursor-default opacity-85" : ""}`}
              >
                {cardInner(card)}
              </Link>
            )
          )}
        </div>
      </div>

      {/* Feature panels */}
      {activeModal === "happyHours" && (
        <HappyHourSettings onClose={() => setActiveModal(null)} />
      )}
      {activeModal === "gst" && (
        <GstReportModal onClose={() => setActiveModal(null)} />
      )}
      {activeModal === "marketing" && (
        <MarketingPanel onClose={() => setActiveModal(null)} />
      )}
      {activeModal === "waste" && (
        <WastePanel onClose={() => setActiveModal(null)} />
      )}
      {activeModal === "feedback" && (
        <FeedbackPanel onClose={() => setActiveModal(null)} />
      )}
      {activeModal === "branches" && (
        <BranchesPanel onClose={() => setActiveModal(null)} />
      )}
    </div>
  );
};

export default AdminPanel;
